//React
import React from 'react';
import { Global } from '@emotion/react';
import {
  BILLING_DATA_GRID_CLASS_NAME,
  billingDataGridStyles,
} from 'styles/BillingDataGridStyles';

//Mantine
import {
  MRT_TableOptions,
  MantineReactTable,
  useMantineReactTable,
} from 'mantine-react-table';

//Refine
interface IProps {
  data: MRT_TableOptions<any>;
  onTableReady?: (table: any) => void; // loosely typed to avoid importing full type here
}

import styles from 'styles/TableStyles.module.css';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultToolBarInternalActions } from './DefaultToolbarInternalActions';

/**
 * Returns a MantineReactTable component with the given data, columns and options.
 *
 * @param data - The data to display in the table. Data must be memoized. Options can be found at https://www.mantine-react-table.com/docs/api/table-options
 *
 * @returns A MantineReactTable component with the given data and columns.
 */

const BillingDataGrid: React.FC<IProps> = (props) => {
  const { data, onTableReady } = props;
  const {
    displayColumnDefOptions,
    renderToolbarInternalActions,
    mantineTableBodyRowProps,
    mantineExpandButtonProps,
  } = data;
  const queryClient = useQueryClient();

  const table = useMantineReactTable({
    ...data,
    state: {
      ...data.state,
      density: 'xs',
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    mantineLoadingOverlayProps: {
      className: styles.overlay,
    },
    mantineTableHeadCellProps: {
      className: styles.MRT_TableHeadCell,
    },
    mantinePaperProps: (props) => {
      const paperProps =
        typeof data.mantinePaperProps === 'function'
          ? data.mantinePaperProps(props)
          : data.mantinePaperProps;

      return {
        ...paperProps,
        className: [
          styles.root,
          BILLING_DATA_GRID_CLASS_NAME,
          paperProps?.className,
        ]
          .filter(Boolean)
          .join(' '),
      };
    },
    enableRowActions:
      data.enableRowActions ?? displayColumnDefOptions !== undefined,
    renderToolbarInternalActions({ table }) {
      if (renderToolbarInternalActions) {
        return renderToolbarInternalActions({ table });
      }
      return (
        <DefaultToolBarInternalActions
          table={table}
          queryClient={queryClient}
        />
      );
    },
    mantineTableBodyRowProps: (props) => {
      const additionalProps =
        typeof mantineTableBodyRowProps === 'function'
          ? mantineTableBodyRowProps(props)
          : {};

      return {
        ...additionalProps,
        className: props.isDetailPanel ? styles.detailPanel : '',
      };
    },
    mantineExpandButtonProps({ table, row }) {
      const additionalProps =
        typeof mantineExpandButtonProps === 'function'
          ? mantineExpandButtonProps({ table, row } as any)
          : mantineExpandButtonProps || {};

      return additionalProps;
    },
  });

  // expose table instance once (or when it changes) to parent if requested
  React.useEffect(() => {
    if (onTableReady) onTableReady(table);
  }, [table, onTableReady]);

  return (
    <>
      <Global styles={billingDataGridStyles} />
      <MantineReactTable table={table} />
    </>
  );
};

export default BillingDataGrid;
